import {
  attr, belongsTo, hasMany, hasOne,
} from 'spraypaint';
import Producer from './Producer';
import ApplicationRecord from './ApplicationRecord';
import Availability from './Availability';
import KeyIngredientDescription from './KeyIngredientDescription';

const MEAT_FOOD_GROUPS = ['Beef', 'Lamb', 'Pork', 'Poultry'];
const FISH_FOOD_GROUPS = ['Fish', 'Shellfish'];
// Note FE does not support Vegetarian as a recipe food group as such recipes don't exist
// but BE code does not prevent such recipes to exist
const PLANT_FOOD_GROUPS = ['Vegetarian', 'Vegan', 'Dairy'];
const FOOD_GROUPS = MEAT_FOOD_GROUPS.concat(FISH_FOOD_GROUPS).concat(PLANT_FOOD_GROUPS);

export class IngredientFoodGroupError extends Error {
  constructor(ingredients) {
    // the detail provided by this custom error allows us to fix errors in our
    // back office system easily quickly and efficiently
    const message = `
      The following ingredients are missing a food_group:
      ${ingredients
    .map((ingredient) => `${ingredient.name}, id: ${ingredient.id}`)
    .join('\n')}.
      These Ingredients may need to be edited in the back office system.`;
    super(message);
  }
}

const Recipe = ApplicationRecord.extend({
  attrs: {
    allergens: attr({ type: Array, persist: false }),
    possible_allergens: attr({ type: Array, persist: false }),
    title: attr(),
    before_you_start: attr({type: String}),
    slug: attr({ type: String }),
    description: attr(),
    food_group: attr({ type: String }),
    cuisine: attr({ type: String }),
    season: attr({ type: String }),
    special: attr({ type: String }),
    key_ingredient_description: attr({ type: String }),
    oil_grams_1_portion: attr({ type: Number }),
    oil_grams_2_portion: attr({ type: Number }),
    oil_grams_4_portion: attr({ type: Number }),
    badges: attr({ type: Array, persist: false }),
    manual_badges: attr({ type: Array }),
    cook_along_video_url: attr({ type: String }),
    status: attr({ type: String }),
    carbon_rating: attr({ type: String }),
    spiciness_level: attr({ type: Number }),
    steps: hasMany({ type: 'recipes-recipe_steps' }),
    tags: hasMany({ type: 'tags-tags' }),
    images: hasMany({ type: 'recipes-recipe_images' }),
    feedbacks: hasMany({ type: 'recipes-feedbacks' }),
    notes: hasMany({ type: 'recipes-recipe_notes' }),
    availabilities: hasMany({ type: Availability, persist: false }),
    recipe_portion_components: hasMany({
      type: 'recipes-recipe_portion_components',
    }),
    macros_per_person_for_1_portion: hasOne({
      type: 'recipes-macros_per_person_for_1_portions',
    }),
    macros_per_person_for_2_portion: hasOne({
      type: 'recipes-macros_per_person_for_2_portions',
    }),
    macros_per_person_for_4_portion: hasOne({
      type: 'recipes-macros_per_person_for_4_portions',
    }),
    macros_per_100g_for_1_portion: hasOne({
      type: 'recipes-macros_per_100g_for_1_portions',
    }),
    macros_per_100g_for_2_portion: hasOne({
      type: 'recipes-macros_per_100g_for_2_portions',
    }),
    macros_per_100g_for_4_portion: hasOne({
      type: 'recipes-macros_per_100g_for_4_portions',
    }),
    ingredients: hasMany({ type: 'recipes-ingredients' }),
    components: hasMany({ type: 'recipes-components' }),
    key_producer: belongsTo({ type: Producer }),
    overlay_image: belongsTo({ type: 'recipes-overlay_images' }),
    related_key_ingredient_description: belongsTo({ type: KeyIngredientDescription, persist: false }),
    average_rating: attr({ type: Number })
  },
  static: {
    endpoint: '/recipes',
    MEAT_FOOD_GROUPS,
    FISH_FOOD_GROUPS,
    FOOD_GROUPS,
    jsonapiType: 'recipes-recipes',
  },
  methods: {
    assertValidIngredients() {
      /**
       * foodGroup and foodType calculations require that we include the recipes ingredients
       * If we forget to request the ingredients along with the recipe these computed values will be wrong
       * which will cause application errors as we rely on valid foodGroups and foodTypes
       * for things like search filtering
       */
      if (!this.ingredients.length) {
        throw new Error(
          'To call this function, you must include ingredients when fetching the recipes',
        );
      }

      const invalidIngredients = this.ingredients.filter(
        (ingredient) => ingredient.food_group === undefined,
      );

      /*
       * As with the above check we need to ensure all Ingredients have a valid food_group
       * As food_groups are entered within the back office system we could, due to human error
       * have a recipe in which ingredients have no food_group, In this case we cannot
       * accurately discern if the recipe is vegan etc
       */
      if (invalidIngredients.length) {
        throw new IngredientFoodGroupError(invalidIngredients);
      }
    },
    /**
     * This is only here for documentation. The method itself is non-functional
     * as it shadows the attribute name.
     *
     * @deprecated Please use recipe.allergens (attribute not method call)
     */
    allergens() {
      return this.allergens;
    },
    availablePortions() {
      const portionCounts = this.recipe_portion_components.map(
        (rpc) => rpc.portion_count_per_meal,
      );
      return [...new Set(portionCounts)].sort();
    },
    cookingTime() {
      if (this.steps.some((step) => step.duration_minutes === undefined)) {
        throw new Error('Recipe steps needs to include duration_minutes');
      }

      return this.steps.reduce(
        (totalCookingTime, step) => totalCookingTime + step.duration_minutes,
        0,
      );
    },
    prepTime() {
      if (this.steps.some((step) => step.prep_time_minutes === undefined)) {
        throw new Error('Recipe steps needs to include prep_time_minutes');
      }

      return this.steps.reduce(
        (totalPrepTime, step) => totalPrepTime + step.prep_time_minutes,
        0,
      );
    },

    /**
     * @deprecated Please use recipe.food_group (attribute not method call)
     */
    foodGroup() {
      return this.food_group;
    },
    hasMeatContent() {
      this.assertValidIngredients();
      return this.ingredients.some((ingredient) => Recipe.MEAT_FOOD_GROUPS.includes(ingredient.food_group));
    },
    hasFishContent() {
      this.assertValidIngredients();
      return this.ingredients.some((ingredient) => Recipe.FISH_FOOD_GROUPS.includes(ingredient.food_group));
    },
    hasOnlyVeganContent() {
      this.assertValidIngredients();
      return this.ingredients.every(
        (ingredient) => ingredient.food_group === 'Vegan',
      );
    },
    foodType() {
      this.assertValidIngredients();
      switch (true) {
        case this.hasMeatContent():
          return 'Carnivore';
        case this.hasFishContent():
          return 'Pescetarian';
        case this.hasOnlyVeganContent():
          return 'Vegan';
        default:
          return 'Vegetarian';
      }
    },
  },
});

export default Recipe;
